/**
 * Chargement et pilotage de Google Identity Services (bouton « Continuer avec
 * Google »).
 *
 * Le script est injecté à la demande — seules les pages d'inscription et de
 * connexion en ont besoin — et une seule fois par onglet.
 */

const GSI_SRC = "https://accounts.google.com/gsi/client";
const GSI_SCRIPT_ID = "tilmicode-google-identity";

type GoogleCredentialResponse = { credential?: string };

type GoogleAccountsId = {
    initialize: (config: {
        client_id: string;
        callback: (response: GoogleCredentialResponse) => void;
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
        ux_mode?: "popup" | "redirect";
    }) => void;
    renderButton: (
        parent: HTMLElement,
        options: {
            type?: "standard" | "icon";
            theme?: "outline" | "filled_blue" | "filled_black";
            size?: "small" | "medium" | "large";
            text?: "signin_with" | "signup_with" | "continue_with" | "signin";
            shape?: "rectangular" | "pill" | "circle" | "square";
            width?: number;
            locale?: string;
            logo_alignment?: "left" | "center";
        }
    ) => void;
    disableAutoSelect: () => void;
};

declare global {
    interface Window {
        google?: { accounts?: { id?: GoogleAccountsId } };
    }
}

let loader: Promise<GoogleAccountsId> | null = null;

/** Charge le SDK Google (idempotent) et résout sur `google.accounts.id`. */
export const loadGoogleIdentity = (): Promise<GoogleAccountsId> => {
    if (window.google?.accounts?.id) {
        return Promise.resolve(window.google.accounts.id);
    }
    if (loader) return loader;

    loader = new Promise<GoogleAccountsId>((resolve, reject) => {
        const onReady = () => {
            const api = window.google?.accounts?.id;
            if (api) resolve(api);
            else reject(new Error("Google Identity Services indisponible"));
        };

        const existing = document.getElementById(GSI_SCRIPT_ID) as HTMLScriptElement | null;
        if (existing) {
            existing.addEventListener("load", onReady, { once: true });
            existing.addEventListener("error", () => reject(new Error("Chargement du SDK Google impossible")), {
                once: true,
            });
            return;
        }

        const script = document.createElement("script");
        script.id = GSI_SCRIPT_ID;
        script.src = GSI_SRC;
        script.async = true;
        script.defer = true;
        script.onload = onReady;
        script.onerror = () => {
            // Permet une nouvelle tentative si le réseau revient.
            loader = null;
            reject(new Error("Chargement du SDK Google impossible"));
        };
        document.head.appendChild(script);
    });

    return loader;
};

export type GoogleButtonOptions = {
    clientId: string;
    container: HTMLElement;
    onCredential: (idToken: string) => void;
    onError?: (error: Error) => void;
    /** "signup_with" sur l'inscription, "continue_with" sur la connexion. */
    text?: "signin_with" | "signup_with" | "continue_with";
    locale?: string;
    width?: number;
};

/**
 * Initialise le SDK et rend le bouton Google dans `container`.
 * Retourne une fonction de nettoyage qui vide le conteneur.
 */
export const renderGoogleButton = async (options: GoogleButtonOptions): Promise<() => void> => {
    const { clientId, container, onCredential, onError, text = "continue_with", locale, width } = options;

    try {
        const accountsId = await loadGoogleIdentity();
        accountsId.initialize({
            client_id: clientId,
            callback: (response) => {
                if (response?.credential) onCredential(response.credential);
                else onError?.(new Error("Aucune identité renvoyée par Google"));
            },
            // Pas de connexion silencieuse : sur une page d'inscription, l'action
            // doit rester un choix explicite du visiteur.
            auto_select: false,
            cancel_on_tap_outside: true,
            ux_mode: "popup",
        });

        container.innerHTML = "";
        accountsId.renderButton(container, {
            type: "standard",
            theme: "outline",
            size: "large",
            shape: "pill",
            logo_alignment: "center",
            text,
            locale,
            width,
        });
    } catch (err) {
        onError?.(err instanceof Error ? err : new Error(String(err)));
    }

    return () => {
        container.innerHTML = "";
    };
};
